import React, { createContext, useCallback, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { UserLogin } from 'src/services';
import { setUserInfo } from 'src/store/user/action';
import { useDispatch } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
const initialState = {
  user: null,
  permissions: [],
  isInitialised: false,
  isAuthenticated: false,
  userType: 'admin'
};

const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 < Date.now();
  } catch (e) {
    return true;
  }
};

const setSession = (accessToken, accessUser, permissions) => {
  return new Promise((resolve) => {
    if (accessToken) {
      localStorage.setItem('@accessToken', accessToken);
      localStorage.setItem('@accessUser', accessUser);

      if (permissions !== undefined) {
        localStorage.setItem(
          '@permissions',
          JSON.stringify(permissions)
        );
      }

      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      localStorage.removeItem('@accessToken');
      localStorage.removeItem('@accessUser');
      localStorage.removeItem('@permissions');
      delete axios.defaults.headers.common.Authorization;
    }
    resolve();
  });
};


const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      const { isAuthenticated, user, permissions } = action.payload;
      const userType = user?.role === 'employee' || user?.isEmployee ? 'employee' : 'admin';
      return { ...state, isAuthenticated, isInitialised: true, user, permissions, userType };
    }
    case 'LOGIN': {
      const { user, permissions } = action.payload;
      const userType = user?.role === 'employee' || user?.isEmployee ? 'employee' : 'admin';
      return { ...state, isAuthenticated: true, user, permissions, userType };
    }
    case 'LOGOUT': {
      return { ...state, isAuthenticated: false, user: null, permissions: [], userType: 'admin' };
    }
    case 'REGISTER': {
      const { user } = action.payload;
      const userType = user?.role === 'employee' || user?.isEmployee ? 'employee' : 'admin';
      return { ...state, isAuthenticated: true, user, userType };
    }

    default:
      return state;
  }
};

const AuthContext = createContext({
  ...initialState,
  method: 'JWT',
  login: () => { },
  logout: () => { },
  register: () => { },
  checklogin: () => { },
  loginEmploye: () => { }

});

const normalizeUser = (user) => ({
  ...user,
  image: user?.image || user?.avatar || user?.photo || null,
  firstname: user?.firstname || user?.name,
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const dispatchM = useDispatch();
  const login = async (email, password) => {
    try {
      const response = await UserLogin.UserLogin({ email, password });
      if (response) {
        const fullUserData = normalizeUser({
          ...response.user,
          name: response.user?.firstname,
          lastname: response.user?.lastname,
          image: response.user?.image || response.additionalData?.image,
        });

        await Promise.all([
          setSession(response.token, JSON.stringify(fullUserData), response.permissions),
          dispatch({ type: 'LOGIN', payload: { user: fullUserData, permissions: response.permissions || [] } })
        ]);
        document.body.classList.remove('employee-mode');
        return true;
      } else {
        setSession();
        return false;
      }
    } catch (error) {
      console.error('Login failed:', error);
      setSession();
      return false;
    }

  };
  const loginEmploye = async (email, password) => {
    try {
      const response = await UserLogin.UserLoginEmploye({ email, password });

      if (!response?.user) return false;

      const fullUserData = normalizeUser({
        ...response.user
      });

      await setSession(response.token, JSON.stringify(fullUserData), response.permissions);

      dispatch({ type: 'LOGIN', payload: { user: fullUserData } });

      dispatchM(setUserInfo(fullUserData));

      document.body.classList.add('employee-mode');

      return true;
    } catch (error) {
      console.error('Login failed:', error);
      setSession();
      return false;
    }
  };
  const register = async (email, username, password) => {
    const response = await axios.post('/api/auth/register', { email, username, password });
    const { user } = response.data;

    dispatch({ type: 'REGISTER', payload: { user } });
  };

  const logout = useCallback(async () => {

    await setSession();

    document.body.classList.remove('employee-mode');

    dispatch({
      type: 'LOGOUT'
    });

    window.location.href = '/login';

  }, []);

  const checklogin = () => {
    const accessToken = localStorage.getItem('@accessToken');

    if (!accessToken || isTokenExpired(accessToken)) {
      logout();
      return false;
    }

    const raw = localStorage.getItem('@accessUser');

    if (!raw) {
      logout();
      return false;
    }

    const userData = JSON.parse(raw);

    dispatch({
      type: 'INIT',
      payload: {
        isAuthenticated: true,
        user: userData,
        permissions: JSON.parse(
          localStorage.getItem('@permissions') || '[]'
        )
      }
    });

    return true;
  };

  useEffect(() => {
    (async () => {
      const accessToken = localStorage.getItem('@accessToken');
      const storedPermissions = JSON.parse(
        localStorage.getItem('@permissions') || '[]'
      );

      // Vérification token absent ou expiré
      if (!accessToken || isTokenExpired(accessToken)) {
        setSession();
        document.body.classList.remove('employee-mode');

        dispatch({
          type: 'INIT',
          payload: {
            isAuthenticated: false,
            user: null,
            permissions: []
          }
        });

        return;
      }

      try {
        const raw = localStorage.getItem('@accessUser');
        const userData = raw ? JSON.parse(raw) : null;

        if (userData) {
          dispatch({
            type: 'INIT',
            payload: {
              isAuthenticated: true,
              user: userData,
              permissions: storedPermissions
            }
          });

          if (userData.role === 'employee' || userData.isEmployee) {
            document.body.classList.add('employee-mode');
          } else {
            document.body.classList.remove('employee-mode');
          }

        } else {
          setSession();

          dispatch({
            type: 'INIT',
            payload: {
              isAuthenticated: false,
              user: null,
              permissions: []
            }
          });
        }

      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);

        setSession();

        dispatch({
          type: 'INIT',
          payload: {
            isAuthenticated: false,
            user: null,
            permissions: []
          }
        });
      }
    })();

  }, []);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem('@accessToken');

      if (token && isTokenExpired(token)) {
        logout();
      }
    };

    const interval = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(interval);

  }, [logout]);

  // SHOW LOADER
  if (!state.isInitialised) return <p>hee</p>;

  return (
    <AuthContext.Provider value={{ ...state, method: 'JWT', login, logout, register, checklogin, loginEmploye }}>
      {children}
    </AuthContext.Provider>
  );
};
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
